import { KalturaPurchaseStatus, KalturaRuleType } from '@kaltura-ott/ott-client-types/api/types';
import { KalturaUserAssetRule } from '@kaltura-ott/ott-client-types/api/types/KalturaUserAssetRule';
import { FormatKalturaProductPricePpv, KuxBookmark, KuxMediaAssetType } from '@kaltura-ott/tvpil';
import { computed, observable } from 'mobx';

import { MediaAsset } from './MediaAsset';

export enum SubscriptionType {
  addonForPurchasedBase = 'addonForPurchasedBase',
  base = 'base',
  addonForOtherBase = 'addonForOtherBase',
}
export interface SelectedSubscription {
  packageId: number;
  baseTitle?: string;
  addonTitle?: string;
  amount: number;
  type: SubscriptionType;
}
export abstract class PlayableAsset<T extends KuxMediaAssetType = KuxMediaAssetType> extends MediaAsset<T> {
  @observable rules?: KalturaUserAssetRule[] = [];
  @observable price?: FormatKalturaProductPricePpv;
  @observable bookmark?: KuxBookmark;
  @observable subscriptionIds: string[] = [];
  @observable selectedSubscription?: SelectedSubscription;

  @computed get isPurchased(): boolean | undefined {
    if (!this.price) {
      return undefined;
    }
    return ![
      KalturaPurchaseStatus.forPurchase,
      KalturaPurchaseStatus.forPurchaseSubscriptionOnly,
      KalturaPurchaseStatus.pendingEntitlement,
    ].includes(this.price.purchaseStatus);
  }

  @computed get isPurchasable(): boolean | undefined {
    if (this.isPurchased === undefined) {
      return undefined;
    }
    return !this.isPurchased;
  }

  @computed get isPurchasePending(): boolean | undefined {
    if (!this.price) {
      return undefined;
    }
    return this.price.purchaseStatus === KalturaPurchaseStatus.pendingEntitlement;
  }

  setPrice(priceValue: FormatKalturaProductPricePpv | undefined) {
    if (!priceValue) {
      return;
    }
    this.price = priceValue;
  }

  abstract get mediaFileId(): number | undefined;
  abstract setupRules(): Promise<void>;
  abstract get isPlayable(): boolean;

  @computed get isAdult(): boolean | undefined {
    return this.data?.adult;
  }

  public getRuleByType(type: KalturaRuleType): KalturaUserAssetRule | undefined {
    return computed(() => {
      return this.rules?.find((rule) => rule.ruleType === type);
    }).get();
  }

  @computed get isGeoBlocked(): boolean {
    return !!this.getRuleByType(KalturaRuleType.geo);
  }

  @computed get geoBlockingProperties(): {
    description?: string;
    name?: string;
  } {
    const rule = this.getRuleByType(KalturaRuleType.geo);
    if (rule) {
      return {
        description: rule.description,
        name: rule.name,
      };
    }
    return {};
  }
}
